import { useMemo, useRef, useState } from 'react';
import type { ChatUiFlightOffer } from '@voll-intelligence/genui-contract';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { ScreenBarClose, useScreenBarTrail } from '../../../shared/ui/screen_bar_parts.js';
import { ScreenDialog } from '@voll-intelligence/ui-kit/ui/screen-dialog';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { FlightExplorer } from './explorer.js';
import {
  sameTripGeometry,
  tripGeometryOf,
  type ExplorerConfirm,
  type ExplorerPick,
} from '@voll-intelligence/intelligence-ui-kit';
import type { FlightExplorerRequest } from '@voll-intelligence/intelligence-ui-kit';
import '@voll-intelligence/intelligence-ui-kit/flight-explorer.css';

/**
 * A TELA EM DIÁLOGO do explorer no chat — a MESMA tela da bancada até a escolha
 * da tarifa: a barra do topo com o ✕ (slot `chrome`), a pill de contexto
 * centrada (`bandAsPill`, compacta no celular) e a mala à direita, em TODA
 * etapa, inclusive na tela de tarifas (padronização de 30/09, #3875); o que
 * difere é o verbo do CTA final (`faresCtaLabel`): aqui "Confirmar", porque o
 * pick volta à conversa — na bancada "Ir para checkout". Cobre a janela inteira
 * por cima da conversa.
 *
 * É a `ScreenDialog`: modal, trap, Esc e o foco de volta a quem abriu são do
 * Base UI. O Esc com o planner da pill aberto é dele (a regra das camadas
 * internas); na tela de tarifas ele não é de ninguém (#2792) — fechar
 * descartaria a busca e os voos escolhidos, e o caminho de volta ali é o
 * "Alterar voo". A mala do host (#3301) e o detalhe do voo abrem DENTRO da
 * tela.
 *
 * O ciclo de vida é do HOST: fechar no confirm é decisão dele (`onConfirm` não
 * fecha sozinho — o multi fecha ao adotar o pick, o binário fecha ao
 * despachar). O seam de busca vem do `GenUiConfig.flightSearch` (fallback do
 * explorer — a casca não passa `search`). Sem o seam a casca rende null: o
 * explorer dentro renderia null (cinto de segurança dele) e sobraria uma tela
 * opaca sem conteúdo nem ✕ cobrindo o app inteiro.
 */
export function FlightExplorerDialog({
  request,
  seedOffers,
  reuseAnchorOfferId,
  initialLeg,
  initialChosen,
  initialPicksDiscarded,
  replacesMultiCard,
  legIndex,
  readOnly,
  onConfirm,
  onClose,
}: Readonly<{
  request: FlightExplorerRequest;
  /** Lista já em mãos do card, POR PERNA (mesma regra do explorer). */
  seedOffers?: ChatUiFlightOffer[][];
  /** Repasse cru — ver `FlightExplorerProps.reuseAnchorOfferId`. */
  reuseAnchorOfferId?: string;
  /** Perna em que o stepper abre — o trecho de onde a pessoa clicou. */
  initialLeg?: number;
  /** Trechos que o card já fechou. */
  initialChosen?: Partial<Record<number, ExplorerPick>>;
  /** Poda pré-mount (ver `initialExplorerState.initialPicksDiscarded`). */
  initialPicksDiscarded?: boolean;
  /** Repasse cru — ver `FlightExplorerProps.replacesMultiCard`. */
  replacesMultiCard?: boolean;
  /**
   * Presente ⇒ esta tela explora UMA perna: o confirm `single` é re-rotulado
   * `{kind:'leg', legIndex}` com UM fareId (cada trecho é one-way). O reducer só
   * produz single/multi — o rótulo por perna é da casca, e vale SÓ no caminho
   * não-divergente (ver o comparador no `confirm` abaixo).
   */
  legIndex?: number;
  readOnly?: boolean;
  onConfirm: (confirm: ExplorerConfirm) => void;
  onClose: () => void;
}>) {
  const { t } = useGenUiI18n();
  const { flightSearch, takeoverTrailing } = useGenUiConfig();
  const trail = useScreenBarTrail('flight', onClose);
  // A fila de chips do celular mora na segunda linha DESTA barra, não na do
  // shell que a tela cobre: o slot vai ao explorer por prop.
  const [chipsSlot, setChipsSlot] = useState<HTMLElement | null>(null);
  const chrome = useMemo(() => ({ trail, setChipsSlot }), [trail]);
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  if (!flightSearch) return null;

  const confirm = (c: ExplorerConfirm) => {
    // O re-rótulo por perna vale SÓ no caminho NÃO-divergente: com a faixa
    // destravada, um `single` re-formado (outra data/rota, ou a aba virada pra
    // ida-e-volta) precisa chegar INTEIRO ao host — achatar a união pra UM
    // fareId aqui perderia o `returnFareId` de um RT split (spec: "a casca não
    // achata mais no divergente").
    if (
      legIndex === undefined ||
      c.kind !== 'single' ||
      !sameTripGeometry(c.trip, tripGeometryOf(request))
    ) {
      onConfirm(c);
      return;
    }
    const fareId = 'bundledFareId' in c.fares ? c.fares.bundledFareId : c.fares.outboundFareId;
    onConfirm({ kind: 'leg', legIndex, offerId: c.offerId, fareId, summary: c.summary });
  };

  return (
    <ScreenDialog
      label={t('flightExplorer.aria')}
      onClose={onClose}
      popupRef={panelRef}
      initialFocus={closeRef}
      escapeCloses={() => !panelRef.current?.querySelector('.ffs')}
      // No celular a tela é o rolo; a partir de `wide` são três faixas e só a
      // do meio rola (`.fex`). O `.fex-takeover` leva o empurrão do detalhe do
      // voo (`explorer.css`).
      className="fex-takeover block overflow-auto overscroll-contain bg-(--v2-surface) wide:grid wide:grid-rows-[minmax(0,1fr)] wide:overflow-hidden"
    >
      <FlightExplorer
        request={request}
        seedOffers={seedOffers}
        reuseAnchorOfferId={reuseAnchorOfferId}
        initialLeg={initialLeg}
        initialChosen={initialChosen}
        initialPicksDiscarded={initialPicksDiscarded}
        replacesMultiCard={replacesMultiCard}
        takeover={chrome}
        toolbarSlot={chipsSlot}
        // Paridade com a bancada (#2129/#2130): a banda sai da coluna e o
        // contexto vira PILL na barra da tela; os segmentos expandem o
        // planner inline com a lista preservada atrás.
        bandAsPill
        // O canto direito do chrome é do host (#3301): a mala da viagem, que o
        // tela cobre no top bar do shell. Ausente no config ⇒ nada.
        trailing={takeoverTrailing}
        // O pick da tela de tarifas volta à CONVERSA (o host despacha e fecha a
        // casca); o checkout é outro turno — daí "Confirmar", não "Ir para
        // checkout".
        faresCtaLabel="flightExplorer.confirm"
        chrome={<ScreenBarClose ref={closeRef} label={t('common.close')} onClick={onClose} />}
        readOnly={readOnly}
        onConfirm={confirm}
      />
    </ScreenDialog>
  );
}
